import { FC, Suspense, lazy, useMemo } from 'react';
import { AttachmentFile, UploadAttachmentFile, VoidFn } from 'types/baseTypes';
import { FILE_TYPE } from 'constants/common';
import ImagePreview from 'components/image-preview/ImagePreview';

const PDFPreview = lazy(() => import('components/pdf-preview/PDFPreview'));

interface AttachmentPreviewProps {
  files: UploadAttachmentFile[];
  selectedIndex: number;
  onClose: VoidFn;
}

const AttachmentPreview: FC<AttachmentPreviewProps> = ({ files, selectedIndex, onClose }) => {
  const isImageFile = (fileType?: string) => {
    if (!fileType) return true;
    return (
      fileType === FILE_TYPE.PNG ||
      fileType === FILE_TYPE.JPEG ||
      fileType === FILE_TYPE.JPG ||
      fileType === FILE_TYPE.WEBP ||
      fileType.startsWith('image/')
    );
  };

  const isPDFFile = (fileType?: string) => {
    return fileType === FILE_TYPE.PDF;
  };

  const getFileUrl = (file: UploadAttachmentFile) => {
    if (file?.previewData) {
      return file.previewData;
    }
    return file.urlLink;
  };

  const { previewType, convertedFiles, adjustedIndex, currentFile } = useMemo(() => {
    const currentFileType = files[selectedIndex]?.fileType;

    if (isPDFFile(currentFileType)) {
      return {
        previewType: 'pdf' as const,
        convertedFiles: [],
        adjustedIndex: 0,
        currentFile: files[selectedIndex]
      };
    }

    if (isImageFile(currentFileType)) {
      const imageFiles = files?.filter(file => isImageFile(file.fileType));
      const imageIndex = imageFiles?.findIndex(
        file => (file.id || file.tempId) === (files[selectedIndex].id || files[selectedIndex].tempId)
      );

      const convertedImages: AttachmentFile[] = imageFiles?.map(file => ({
        id: file.id,
        tempId: file.tempId,
        title: file.title,
        url: file.url,
        description: file.description,
        urlLink: file.previewData || file.urlLink,
        source: file.source,
        fileType: file.fileType
      }));

      return {
        previewType: 'image' as const,
        convertedFiles: convertedImages,
        adjustedIndex: imageIndex >= 0 ? imageIndex : 0,
        currentFile: null
      };
    }

    return {
      previewType: 'none' as const,
      convertedFiles: [],
      adjustedIndex: 0,
      currentFile: null
    };
  }, [files, selectedIndex]);

  if (previewType === 'image' && convertedFiles?.length > 0) {
    return <ImagePreview images={convertedFiles as AttachmentFile[]} selectedIndex={adjustedIndex} onClose={onClose} />;
  }

  if (previewType === 'pdf' && currentFile) {
    return (
      <Suspense fallback={null}>
        <PDFPreview fileUrl={getFileUrl(currentFile)} fileName={currentFile.title} onClose={onClose} />
      </Suspense>
    );
  }

  return null;
};

export default AttachmentPreview;
